/* eslint-disable consistent-return */
/**
 * @file BloodPressureView.jsx
 * @author Fernando Ruano
 * @description Blood Pressure View
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { Empty } from 'antd';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { groupBy } from 'lodash';
import moment from 'moment';

// Components
import AppCardLayout from '@nephro/components/AppCardLayout';

// Assets
import { ReactComponent as HeartIcon } from '@nephro/assets/heartIcon.svg';

// Helpers
import {
  sortNumber,
  sortByDate,
  bloodPressureValidation,
  getMonthShortName,
  getFormatedDate
} from '@nephro/commons/helpers/utils';
import { BLOOD_PRESURE_INDICATOR } from '../helpers/constants';
import usePatientIndicators from '../helpers/usePatientIndicators';

const BloodPressureView = () => {
  const bloodPressureData = usePatientIndicators(BLOOD_PRESURE_INDICATOR);
  const { t: translate } = useTranslation();
  const [xPosition, setXPosition] = React.useState(null);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [fulldata, setFullData] = React.useState(null);
  const [chartDataState, setChartDataState] = React.useState(null);
  const [chartColorsState, setChartColorsState] = React.useState(null);
  const [chartDates, setChartDates] = React.useState(null);

  // const isMobile = window.innerWidth < 768;

  const sortByDatesArray = (list, mode = 'desc') =>
    list.sort((a, b) => {
      const result =
        mode === 'desc' ? new Date(b) - new Date(a) : new Date(a) - new Date(b);
      return result;
    });

  const onValidateColor = (state) =>
    state === 'normal' ? '#003399' : '#db0f00';

  React.useEffect(() => {
    if (bloodPressureData?.length) {
      bloodPressureData?.sort(sortNumber('valor'));
      const bpData = sortByDate(bloodPressureData, 'fecha');

      const groupByDates = groupBy(bpData, 'fecha');

      const datesArray = sortByDatesArray(Object.keys(groupByDates), 'asc');

      const datesArrayFormated = datesArray.map((itm, idx) => {
        const previousYear =
          idx > 0 ? moment(datesArray[idx - 1]).year() : false;
        const currMonth = translate(
          getMonthShortName(new Date(itm).getMonth())
        );
        const currYear = moment(itm).year();

        return `${currMonth} ${
          previousYear !== currYear || idx === 0 ? currYear : ''
        }`;
      });

      const chartData = datesArray.map((itm) => {
        const [low, high] = groupByDates[itm];
        return [low.valor, high.valor];
      });

      const chartColors = chartData?.map((itm) => {
        const [low, high] = itm;
        const stage = bloodPressureValidation(high, low);
        const barColor = onValidateColor(stage);
        return barColor;
      });

      const itemsByFomatedDate = Object.fromEntries(
        datesArrayFormated.map((date, idx) => {
          return [
            date,
            {
              fullDate: getFormatedDate(datesArray[idx], 'DD/MM/YYYY'),
              bloodPressure: chartData[idx]
            }
          ];
        })
      );

      setChartDataState(chartData);
      setChartColorsState(chartColors);
      setChartDates(datesArrayFormated);
      setFullData(itemsByFomatedDate);
    }
  }, [bloodPressureData]);

  const months = chartDates;

  const data = {
    labels: months,
    datasets: [
      {
        label: '',
        data: chartDataState,
        fill: false,
        barThickness: 15,
        backgroundColor: chartColorsState,
        borderColor: chartColorsState,
        borderWidth: 1,
        borderRadius: 100,
        borderSkipped: false,
        pointBorderWidth: 1,
        pointHoverBorderWidth: 6,
        pointHoverBackgroundColor: '#fa8300',
        pointHoverBorderColor: '#fa8300'
      }
    ]
  };

  const validateSystolic = (systolic) => {
    let color = 'text-secondary';
    if (systolic >= 160) color = 'text-stage-3b';
    else if (systolic < 90) color = 'text-stage-3b';
    return color;
  };

  const validateDiastolic = (diastolic) => {
    let color = 'text-secondary';
    if (diastolic >= 100) color = 'text-stage-3b';
    else if (diastolic < 60) color = 'text-stage-3b';
    return color;
  };

  const showTooltip = () => (
    <>
      <div
        style={{
          backgroundColor: 'var(--color-grayletters)',
          padding: 10,
          position: 'relative',
          borderRadius: 10,
          width: 'fit-content',
          left: xPosition,
          transform: 'translateX(-50%)'
        }}>
        <div className="flex flex-row items-end">
          <h3
            className={`${validateSystolic(
              selectedItem?.bloodPressure[1]
            )} text-xl font-bold mb-0`}>
            {selectedItem?.bloodPressure[1]}
          </h3>
          <h3 className="text-xl font-bold mb-0 text-secondary">/</h3>
          <h3
            className={`${validateDiastolic(
              selectedItem?.bloodPressure[0]
            )} text-xl font-bold mb-0`}>
            {selectedItem?.bloodPressure[0]}
          </h3>
          <h3 className="text-sm ml-2 mb-0 mt-2 text-secondary">mmHg</h3>
        </div>
        <h3 className="text-xs mb-0 text-secondary text-center">
          {selectedItem?.fullDate}
        </h3>
      </div>
      <div
        style={{
          borderWidth: 1.8,
          borderColor: '#bac3d4',
          backgroundColor: '#bac3d4',
          paddingBottom: 24,
          marginBottom: -10,
          width: 1,
          position: 'relative',
          left: xPosition
        }}
      />
    </>
  );

  const onSelectItem = (labelKey) => {
    setSelectedItem(fulldata[labelKey]);
  };

  const getLastItem = () => {
    const lastDate = chartDates[chartDates.length - 1];
    return fulldata[lastDate];
  };

  const getPreviousItem = () => {
    const previousDate = chartDates[chartDates.length - 2];
    return fulldata[previousDate];
  };

  const onGetMaxValue = () => {
    const highValues = chartDataState?.map((itm) => {
      return itm[1];
    });
    return Math.round(Math.max(...highValues) / 4);
  };

  return (
    <AppCardLayout>
      <div className="h-full p-5 flex flex-col overflow-y-auto">
        <div className="flex flex-row items-center">
          <HeartIcon width={40} />
          <h3 className="text-xl text-left mb-0 ml-2 text-baxterGray font-bold">
            {translate('dashboard.bloodPressure')}
          </h3>
        </div>
        {!fulldata && !chartDates ? (
          <div className="mt-40">
            <Empty
              style={{ color: '#858fab', fontSize: 15 }}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No hay registro de datos"
            />
          </div>
        ) : (
          <>
            <h3 className="text-base text-left text-baxterGray mt-3">
              {translate('indicators.bloodPressure.description1')}
            </h3>
            <div className="flex flex-col items-center">
              <div className="flex flex-col justify-center w-full md:max-w-3/5">
                {xPosition && selectedItem && showTooltip()}
                <Bar
                  data={data}
                  className="max-w-4xl max-w-md"
                  options={{
                    plugins: {
                      legend: {
                        display: false
                      },
                      tooltip: {
                        enabled: false,
                        backgroundColor: '#e3e8ed',
                        external: (item) => {
                          const xPos = item.tooltip.caretX - 2.5;
                          if (xPos !== xPosition && item.tooltip.dataPoints) {
                            setXPosition(xPos);
                            onSelectItem(item.tooltip.dataPoints[0].label);
                          }
                        }
                      }
                    },
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: 'Fechas'
                        },
                        grid: {
                          color: 'rgba(0, 26, 114, 0.2)',
                          borderDash: [9],
                          lineWidth: 1.5
                        }
                      },
                      y: {
                        title: {
                          display: true,
                          text: 'Tensión arterial - mmHg'
                        },
                        grid: {
                          color: (context) => {
                            if (context.index % 2 === 0) {
                              return 'rgba(0, 26, 114, 0.2)';
                            }
                            return '#FFF';
                          },
                          lineWidth: 2
                        },
                        // max: 200,
                        min: 0,
                        ticks: {
                          stepSize: chartDataState && onGetMaxValue()
                        }
                      }
                    },
                    responsive: true
                  }}
                />
              </div>
              <div className="rounded-lg border w-full max-w-sm border-gray-300 p-2 mt-5">
                <div className="flex flex-row justify-between">
                  <h3 className="text-lg text-left text-baxterGray mb-0">
                    {translate('indicators.last')}: {getLastItem().fullDate}
                  </h3>
                  <div className="flex flex-row">
                    <h3 className="text-lg text-left text-baxterGray font-bold mb-0">
                      {getLastItem().bloodPressure[1]}/
                      {getLastItem().bloodPressure[0]}
                    </h3>
                    <h3 className="text-lg text-left text-baxterGray ml-1 mb-0">
                      mmHg
                    </h3>
                  </div>
                </div>
                <div className="flex flex-row justify-between mt-5">
                  <h3 className="text-lg text-left text-baxterGray mb-0">
                    {translate('indicators.previous')}:{' '}
                    {getPreviousItem().fullDate}
                  </h3>
                  <div className="flex flex-row">
                    <h3 className="text-lg text-left text-baxterGray font-bold mb-0">
                      {getPreviousItem().bloodPressure[1]}/
                      {getPreviousItem().bloodPressure[0]}
                    </h3>
                    <h3 className="text-lg text-left text-baxterGray ml-1 mb-0">
                      mmHg
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AppCardLayout>
  );
};

/**
 * Component definition of proptypes.
 */
BloodPressureView.propTypes = {
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired,
  calendarActions: PropTypes.shape({
    getPatientAppointmentsArray: PropTypes.func.isRequired
  }).isRequired,
  loading: PropTypes.bool
};

export default BloodPressureView;
